// Shared between the creation form (client) and the itinerary page (server)
// so the two never drift: the form only ever sends these known slugs, and
// the server only ever maps known slugs to fixed prompt phrases. Free-text
// from the URL is never forwarded into the AI prompt.

export interface DurationOption {
  value: string;
  label: string;
}

export const durationOptions: DurationOption[] = [
  { value: "2", label: "Weekend" },
  { value: "3", label: "3 Days" },
  { value: "7", label: "1 Week" },
];

export interface VibeOption {
  value: string;
  label: string;
  promptPhrase: string;
}

export const vibeOptions: VibeOption[] = [
  {
    value: "adventure",
    label: "Adventure",
    promptPhrase: "adventurous outdoor activities and unique experiences",
  },
  {
    value: "relaxation",
    label: "Relaxation",
    promptPhrase: "relaxing, low-key experiences with plenty of downtime",
  },
  {
    value: "foodie",
    label: "Foodie",
    promptPhrase: "food, local cuisine, and top-rated restaurants",
  },
  {
    value: "family",
    label: "Family-Friendly",
    promptPhrase: "family-friendly activities suitable for all ages",
  },
];

export const DEFAULT_TRAVEL_DURATION = 3;
const MAX_TRAVEL_DURATION = 14;

export function parseTravelDuration(daysParam?: string | null): number {
  const parsed = parseInt(daysParam ?? "", 10);
  if (!Number.isFinite(parsed) || parsed < 1) return DEFAULT_TRAVEL_DURATION;
  return Math.min(parsed, MAX_TRAVEL_DURATION);
}

// Backend expects the same toDateString() shape it has always defaulted to
// (e.g. "Mon Jul 14 2026"); an invalid/missing param falls back to undefined
// so callers can apply their own "today" default.
//
// The date input gives a plain "YYYY-MM-DD" string. Parsing that directly
// with `new Date(str)` treats it as UTC midnight, which then renders as the
// *previous* day once formatted in a server timezone behind UTC (e.g. the
// 10th becomes the 9th in US timezones). Building the Date from explicit
// local year/month/day components avoids that shift entirely.
export function formatStartDateForBackend(
  dateParam?: string | null
): string | undefined {
  if (!dateParam) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateParam);
  if (!match) return undefined;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toDateString();
}

export function buildVibeInstruction(vibeParam?: string | null): string {
  if (!vibeParam) return "";
  const slugs = vibeParam
    .split(",")
    .map((v) => v.trim().toLowerCase())
    .filter((v, index, arr) => v && arr.indexOf(v) === index);

  const phrases = slugs
    .map((slug) => vibeOptions.find((opt) => opt.value === slug)?.promptPhrase)
    .filter((phrase): phrase is string => Boolean(phrase));

  if (phrases.length === 0) return "";
  return `Focus the itinerary on ${phrases.join(" and ")}.`;
}
